import { type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { listFactors, FACTORS_QUERY_KEY } from '../api/mfa';
import { isUsableFactor } from '../api/types';
import { useAccessToken } from '../auth/useAuth';
import { mfaEnrollmentEnabled } from '../lib/appEnv';
import { EnrollmentWall } from './EnrollmentWall';
import { FullPageLoading } from '../components/FullPageLoading';

/**
 * The hard wall (Stream 4 Phase 1): a signed-in user with no usable MFA factor
 * is routed to the enrollment flow before anything else in the authenticated
 * app renders — no dashboard, no vault, not even the workspace picker.
 * Deliberately no "skip for now": appcore's floor (once enabled per workspace,
 * appcore docs/28 §2/§9) is the actual security control that blocks data
 * without a factor; this gate exists to make that graceful instead of a wall
 * of 403s, and MUST NOT be treated as the boundary itself.
 *
 * Mounted above `WorkspaceProvider` (identity-scoped, not workspace-scoped —
 * `/me/factors` carries no `X-Workspace-Id`), so enrollment works even for a
 * founder who hasn't created a workspace yet.
 *
 * Fails toward "keep the wall up" on anything short of a confirmed non-empty
 * factor list: while loading, on a transient fetch failure, or if the payload
 * ever arrives in an unexpected shape, this renders a quiet loading state
 * rather than either flashing the wall at an already-enrolled user OR letting
 * an unenrolled one through. `retry: 2` gives a flaky connection a couple of
 * chances before that read shows.
 */
export function EnrollmentGate({ children }: { children: ReactNode }) {
  const enabled = mfaEnrollmentEnabled();
  const token = useAccessToken();

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: FACTORS_QUERY_KEY,
    queryFn: () => listFactors(token!),
    // Don't even probe /me/factors when the wall is disabled for this build.
    enabled: enabled && !!token,
    staleTime: 60_000,
    retry: 2,
  });

  // Controlled enablement: until MFA enrollment is turned on for this deployment
  // (alongside flooring the workspace + updating e2e), the gate is a pass-through
  // so it never walls an existing/unenrolled session out prematurely.
  if (!enabled) return <>{children}</>;

  if (!token) return null; // ProtectedRoute hasn't confirmed auth yet

  if (isLoading) return <FullPageLoading />;

  if (isError) {
    return (
      <div className="mx-auto max-w-[420px] pt-[10vh] text-center">
        <p className="text-[13px] text-slate font-sans">
          We couldn’t check your sign-in keys just now. Check your connection and try again.
        </p>
        <button
          onClick={() => void refetch()}
          className="mt-4 px-5 py-2 text-[11px] font-bold rounded-token border-none cursor-pointer bg-navy text-cream uppercase tracking-[0.1em] font-sans"
        >
          Try again
        </button>
      </div>
    );
  }

  const usable = (data ?? []).filter(isUsableFactor);
  if (usable.length === 0) {
    return <EnrollmentWall onEnrolled={() => void refetch()} />;
  }

  return <>{children}</>;
}
